import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleDot, Circle } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDay, fmtMoney } from "@/lib/format";
import { callbackUrl, redirectToPaystack } from "@/lib/checkout";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { BackHeader } from "@/components/back-header";
import { MembershipStatusCard } from "@/components/membership-card";
import { CheckCircle2 } from "lucide-react";
import { Button, ChipGroup, ErrorState, Loading, Pill, TestModeBanner } from "@/components/ui";

export default function Membership() {
  const toast = useToast();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["membership"], queryFn: () => api("/membership") });
  const [plan, setPlan] = useState(null);
  const [method, setMethod] = useState(["Card"]);
  const [checkout, setCheckout] = useState(null);
  const [busy, setBusy] = useState(null);
  if (isLoading) return <><BackHeader title="Membership" /><Loading /></>;
  if (isError || !data) return <><BackHeader title="Membership" /><ErrorState testId="membership-error" message={error?.message ?? ""} onRetry={() => refetch()} /></>;
  const selected = data.plans.find((p) => p.id === (plan ?? (data.plans.find((x) => x.id === "yearly") ?? data.plans[0])?.id));
  const m = data.membership;
  const renewing = m.status === "active";

  const pay = async () => {
    const mid = data.payment_methods.find((x) => x.label === method[0])?.id;
    if (!mid) return toast("Choose a payment method", "error");
    setBusy("pay");
    try {
      const res = await api("/membership/payments", { method: "POST", body: { plan_id: selected.id, method: mid, channel: "web", callback_url: callbackUrl() } });
      if (!redirectToPaystack(res)) setCheckout(res); // no Paystack key → labelled TEST MODE checkout
    } catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };
  const complete = async (outcome) => {
    setBusy(outcome);
    try {
      const r = await api(`/membership/payments/${checkout.reference}/test-complete`, { method: "POST", body: { outcome } });
      setCheckout(null);
      if (r.payment.status === "success") toast(`Premium active until ${fmtDay(r.membership.ends_at)}`, "success");
      else toast("Payment failed — nothing was charged. Please try again.", "error");
      await Promise.all([refetch(), refresh()]);
      ["connections", "verification", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast(e.message, "error"); } finally { setBusy(null); }
  };

  return (
    <div data-testid="membership-screen" className="mx-auto max-w-3xl">
      <BackHeader title="Membership" />
      <MembershipStatusCard membership={m} />
      <h2 className="mb-1 mt-8 text-[26px]">{renewing ? "Renew early" : "Choose your plan"}</h2>
      {renewing ? <p className="mb-3 text-sm text-muted-foreground">Renewing early adds the time to your current end date — you never lose days.</p> : null}
      <div className="mt-3 grid gap-3 md:grid-cols-3" role="radiogroup" aria-label="Plans">
        {data.plans.map((p) => {
          const on = selected?.id === p.id;
          return (
            <button key={p.id} role="radio" aria-checked={on} data-testid={`membership-plan-${p.id}`} onClick={() => setPlan(p.id)}
              className={`flex items-start gap-3 rounded-[20px] border-2 bg-white p-4 text-left transition-all hover:shadow-md md:flex-col ${on ? "border-primary shadow-md" : "border-border"}`}>
              {on ? <CircleDot className="h-[22px] w-[22px] shrink-0 text-primary" /> : <Circle className="h-[22px] w-[22px] shrink-0 text-muted-foreground" />}
              <span className="flex-1">
                <span className="block text-[15px] font-bold">{p.name}</span>
                <span className="block text-xs text-muted-foreground">{p.days} days{p.includes_verification ? " · Verification included" : ""}</span>
                <span className="mt-1.5 flex flex-wrap gap-1.5">
                  {p.savings_pct ? <Pill testId={`membership-plan-savings-${p.id}`} label={`Save ${p.savings_pct}%`} tone="rose" /> : null}
                  {p.includes_verification ? <Pill label="Free verification" tone="gold" icon={CheckCircle2} /> : null}
                </span>
              </span>
              <span className="font-display text-[26px] font-bold" data-testid={`membership-plan-price-${p.id}`}>{p.price_display}</span>
            </button>
          );
        })}
      </div>
      <h2 className="mb-3 mt-8 text-[22px]">Payment method</h2>
      <ChipGroup testIdPrefix="membership-method" multi={false} options={data.payment_methods.map((x) => x.label)} value={method} onChange={setMethod} />
      <p className="my-3 text-xs text-muted-foreground">One-time payment in Naira — no automatic renewal.</p>
      {data.test_mode ? (
        <>
          <TestModeBanner testId="membership-test-mode-banner" />
          <p className="mt-2 text-xs text-muted-foreground" data-testid="membership-test-mode-note">Online payments (Paystack) aren't connected yet, so checkout is simulated.</p>
        </>
      ) : <p className="text-xs text-muted-foreground" data-testid="membership-paystack-note">You'll be redirected to Paystack's secure checkout (card, bank transfer or USSD), then brought back here.</p>}
      <Button testId="membership-pay-button" title={!selected ? "Choose a plan" : data.test_mode ? `Test pay ${selected.price_display}` : `Pay ${selected.price_display} with Paystack`} disabled={!selected} loading={busy === "pay"} onClick={pay} className="mt-4 w-full sm:w-auto sm:min-w-[240px]" />
      {data.history.length ? (
        <>
          <h2 className="mb-3 mt-10 text-[22px]">Payment history</h2>
          <ul className="flex flex-col gap-2">
            {data.history.map((h) => (
              <li key={h.reference} data-testid={`membership-history-${h.reference}`} className="flex items-center gap-3 rounded-xl border border-border bg-white p-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold">{h.plan_name} · {fmtMoney(h.amount, h.currency)}</p>
                  <p className="truncate text-xs text-muted-foreground">{h.reference} · {fmtDay(h.created_at)}{h.period_end ? ` · until ${fmtDay(h.period_end)}` : ""}</p>
                </div>
                <Pill label={h.status} tone={h.status === "success" ? "success" : h.status === "failed" ? "error" : "neutral"} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <Modal open={!!checkout} onClose={() => setCheckout(null)} title="TEST MODE checkout" testId="membership-checkout-sheet">
        <TestModeBanner />
        <p className="mt-3 text-sm text-muted-foreground" data-testid="membership-checkout-summary">{checkout?.plan_name} · {fmtMoney(checkout?.amount, checkout?.currency)} · ref {checkout?.reference}</p>
        <p className="mb-5 text-sm text-muted-foreground">Choose an outcome to simulate the payment provider.</p>
        <Button testId="membership-checkout-succeed" title="Simulate success" loading={busy === "success"} className="w-full" onClick={() => complete("success")} />
        <Button testId="membership-checkout-fail" title="Simulate failure" variant="outline" loading={busy === "fail"} className="mt-2 w-full" onClick={() => complete("fail")} />
      </Modal>
    </div>
  );
}
